import { useState, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getDetailedErrorMessage } from '@/lib/errorParser';
import { createErrorToast } from '@/components/ui/enhanced-toast';

const OAUTH_STORAGE_KEY = 'outlook_oauth';
const OAUTH_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

interface OAuthData {
  state: string;
  redirectUri: string;
  userId: string;
  timestamp: number;
}

export function useOutlookAuth() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [connecting, setConnecting] = useState(false);

  async function connectOutlook() {
    console.log('[Outlook Auth] Starting connection...');
    
    if (!user) {
      toast({
        title: 'Niet ingelogd',
        description: 'Log eerst in om Outlook te koppelen',
        variant: 'destructive'
      });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: 'Sessie verlopen',
        description: 'Je sessie is verlopen, log opnieuw in',
        variant: 'destructive'
      });
      return;
    }

    const redirectUri = `${window.location.origin}/crm/callback/outlook`;
    const state = crypto.randomUUID();

    // Single consolidated storage key
    const oauthData: OAuthData = {
      state,
      redirectUri,
      userId: user.id,
      timestamp: Date.now()
    };
    sessionStorage.setItem(OAUTH_STORAGE_KEY, JSON.stringify(oauthData));

    console.log('[Outlook Auth] OAuth data stored:', {
      redirectUri,
      state: state.substring(0, 8) + '...'
    });

    const SCOPE = 'Calendars.ReadWrite OnlineMeetings.ReadWrite offline_access User.Read Mail.Read Mail.Read.Shared';

    try {
      const { data, error } = await supabase.functions.invoke('microsoft-calendar-auth', {
        body: {
          action: 'get_auth_url',
          redirectUri,
          scope: SCOPE,
          state
        }
      });

      if (error) throw error;
      
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (error: any) {
      console.error('[Outlook Auth] Error getting auth URL:', error);
      sessionStorage.removeItem(OAUTH_STORAGE_KEY);
      toast({
        title: 'Fout bij verbinden',
        description: error.message,
        variant: 'destructive'
      });
    }
  }

  const handleCallback = useCallback(async (code: string, state: string) => {
    console.log('[Outlook Auth] Processing callback...');
    setConnecting(true);
    
    const errorToast = createErrorToast(toast);
    
    try {
      const oauthDataRaw = sessionStorage.getItem(OAUTH_STORAGE_KEY);
      
      if (!oauthDataRaw) {
        throw new Error('Geen authenticatie data gevonden. Start de koppeling opnieuw.');
      }
      
      const oauthData: OAuthData = JSON.parse(oauthDataRaw);
      
      // Check expiry
      if (Date.now() - oauthData.timestamp > OAUTH_EXPIRY_MS) {
        throw new Error('Authenticatie verlopen. Probeer opnieuw.');
      }
      
      // State validation
      if (state !== oauthData.state) {
        console.error('[Outlook Auth] State mismatch:', {
          received: state.substring(0, 8) + '...',
          expected: oauthData.state.substring(0, 8) + '...'
        });
        throw new Error('Ongeldige authenticatiesessie. Probeer opnieuw te koppelen.');
      }

      console.log('[Outlook Auth] Exchanging code for tokens...');

      const { data, error } = await supabase.functions.invoke('microsoft-calendar-auth', {
        body: {
          action: 'exchange_code',
          code,
          redirectUri: oauthData.redirectUri
        }
      });

      if (error) {
        console.error('[Outlook Auth] Exchange error:', error);
        throw error;
      }

      console.log('[Outlook Auth] ✅ Token exchange successful');

      // Cleanup
      sessionStorage.removeItem(OAUTH_STORAGE_KEY);

      toast({
        title: 'Outlook gekoppeld!',
        description: 'Je agenda is nu beschikbaar in het CRM'
      });

      return true;
    } catch (error: any) {
      console.error('[Outlook Auth] ❌ Callback error:', error);

      const detailedMessage = getDetailedErrorMessage(error);

      errorToast(
        'Koppeling mislukt',
        detailedMessage,
        error,
        { flow: 'outlook_callback' }
      );

      // Cleanup on failure
      sessionStorage.removeItem(OAUTH_STORAGE_KEY);

      return false;
    } finally {
      setConnecting(false);
    }
  }, [toast]);

  async function disconnectOutlook() {
    if (!user) return;

    const { error } = await supabase
      .from('user_settings')
      .update({
        personal_calendar_connected: false,
        personal_microsoft_access_token: null,
        personal_microsoft_refresh_token: null,
        personal_microsoft_token_expiry: null
      })
      .eq('user_id', user.id);

    if (!error) {
      toast({ 
        title: 'Outlook ontkoppeld', 
        description: 'Je agenda is niet meer verbonden' 
      });
    }
  }

  return {
    connectOutlook,
    handleCallback,
    disconnectOutlook,
    connecting
  };
}
